/**
 * Riftgate Arena — the game shell mounted at `/play`.
 *
 * Two screens, and the whole ADR-0005 bundle split hangs off them:
 *
 *   SHELL  this file + core auth/router. Statically imported, <= 150 KB gz.
 *   META   `./meta/menu` — the Training screen. `await import()`, so its
 *          chunk is fetched after first paint.
 *   MATCH  `./match` — Pixi, the renderers, the HUD, the sim. `await import()`,
 *          so **nothing** in the shell chunk ever references `pixi.js`.
 *
 * Those two dynamic imports are the only reason the split exists at build time;
 * `vite.config.ts`'s `manualChunks` only names the chunks that result. Turning
 * either of them into a static import silently moves ~150 KB gz of Pixi into
 * first paint, so `tools/perf/bundle-budget.mjs` fails the build on
 * `staticMatchLeak` if that ever happens.
 *
 * NO GAMEPLAY LOGIC LIVES HERE. This file decides which screen is on, and
 * nothing else.
 */
import { AuthUser } from '../core/state/auth-store';
import type { MatchHandle, MatchOutcome } from './match';
import type { PostMatchReward } from './rewards';
import { iconUrl } from './ui/icon';
import type { MenuSettings, QueueScreen, QueueView } from './meta/menu';
import { a11ySettings, updateA11ySettings } from './a11y';
import { t } from '@/engine/i18n';
// A single closed-enum CONSTANT (the value 29), not the codec: `@rga/proto` is
// `sideEffects:false`, so tree-shaking drops the wire codec the shell must not
// pull in (see `net/session.ts`'s PROTO_VERSION note). It lets the queue screen
// tell a plain timeout ("no opponent") apart from a real transport failure.
import { REASON_QUEUE_TIMEOUT, isFatalReason } from '@rga/proto';
/*
 * THE MODE SEAT — a type-only-plus-one-function import, deliberately.
 *
 * `seat-handoff.ts` imports nothing but a TYPE from the modes chunk, so this
 * costs the shell a few hundred bytes and pulls in no screen. It is what makes
 * Friendly, Draft and Tournament reach this file at all: the modes lobby mints
 * a seat ticket, stashes it, and navigates here; `showQueue` spends it.
 */
import {
  peekPendingSeat,
  takePendingSeat,
  takePendingTraining,
} from './modes/seat-handoff';
/*
 * THE SPECTATE STASH — same shape as the seat one, and safe for the SHELL for
 * the same reason: `spectate-handoff.ts` imports nothing but a TYPE from the
 * social chunk, so this costs the shell a few functions and pulls in no screen.
 * The spectator surface itself arrives through the `import()` in `showSpectate`.
 */
import { takePendingSpectate } from './social/spectate-handoff';
import type { SpectatorTarget } from './social/spectator-session';
/*
 * §9, FROM THE FILE THAT KNOWS WHAT A PLAYER IS ACTUALLY DOING.
 *
 * `queue_enter` and `match_enter` are reported here rather than from the router
 * because entering `/arena` is not entering a queue: the arena opens on a menu,
 * a training difficulty or a lobby seat depending on what is pending, and a
 * router-level guess would overcount the numerator of the funnel's single
 * biggest drop-off. `shell.ts` keeps the collector behind an `import()`, so
 * this costs the SHELL a few functions and no telemetry code.
 */
import { reportDesync, trackFunnel } from './analytics/shell';
/*
 * ADR-0004's CLIENT GATE. `engine/config` shipped complete and unreferenced —
 * every export reachable only from its own barrel and its own test — so a
 * balance table published from the operator console reached the server and
 * never the client, and this file went on hardcoding `cardTableVersion: 1`.
 */
import { activeCardTableVersion, activeCards, ensureCardTableFresh } from './config';
import { createMatchStage, type StageFill } from './layout/match-stage';
/*
 * THE WAIT THE ART NEEDS, AND THE ONLY SCREEN ALLOWED TO SHOW IT.
 *
 * `mountLoadingScreen` is SHELL-safe by construction (see its header) and is
 * already in this chunk via `layout/lazy.ts`, so importing it statically costs
 * the 150 KB budget nothing new. It is imported here rather than reached for
 * dynamically because a loading screen that arrives one chunk late is a blank
 * frame at exactly the moment it exists to cover.
 */
import { mountLoadingScreen } from './rewards/loading-screen';
/*
 * TYPE-ONLY, so `engine/match/**` stays out of the shell's runtime graph
 * (ADR-0005). Every value from that layer below arrives through `import()`.
 */
import type { MatchAssets, SheetTimingTable } from './match/assets';
/*
 * TYPE-ONLY, again for ADR-0005: `runMatchCeremony` itself arrives through the
 * `import('./match/ceremony')` in `showOnlineMatch` and never enters the shell.
 */
import type { MatchCeremony } from './match/ceremony';

type MenuModule = typeof import('./meta/menu');
type MatchModule = typeof import('./match');
type NetModule = typeof import('./match/net');

/**
 * WHERE THE ARENA LIVES. Same origin by default, so a dev server proxying
 * `/rga` and a production Caddy both work with no configuration; `VITE_WS_URL`
 * overrides it for a split deployment.
 */
function arenaOrigin(): string {
  const configured = import.meta.env.VITE_WS_URL as string | undefined;
  if (typeof configured === 'string' && configured !== '') return configured;
  return globalThis.location?.origin ?? '';
}

/** How long to wait between reconnect attempts, and how long to keep trying. */
const RECONNECT_RETRY_MS = 1200;
/** The server forfeits at 15 s; stop a beat early rather than race it. */
const RECONNECT_GRACE_LIMIT_MS = 13_000;

/** The region this build queues in. Latency probing is Phase 6. */
const QUEUE_REGION = 'eu-central';

/* ══════════════════════════════════════════ the two bounds on a search ══ */

/**
 * HOW LONG A SOCKET MAY TAKE TO SAY ANYTHING AT ALL.
 *
 * ── WHAT WAS MEASURED ───────────────────────────────────────────────────────
 * The owner's "Finding an opponent / 0:00 / nothing changes" is this case, and
 * it is nastier than a refused connection. The browser opens
 * `ws://<page origin>/rga/socket`; on the dev stack that origin is the Vite dev
 * server, which has no handler for that path and — unlike a closed port — does
 * not refuse the upgrade. It accepts the TCP connection and never answers the
 * handshake. Probed directly: five seconds, no 101, no HTTP status, no FIN.
 *
 * The browser's `WebSocket` therefore stays in CONNECTING indefinitely: no
 * `open`, no `error`, no `close`. Every one of this screen's exits is an event
 * handler, so not one of them ever ran — `onQueueUpdate` never fired (hence
 * 0:00 before the queue screen counted for itself), `onError` never fired
 * (hence no message), `onClose` never fired (hence not even "lost connection").
 * The screen was not stuck on a slow search; it was waiting on a promise
 * nothing had made.
 *
 * Ten seconds is long against a handshake — the socket is on the same host as
 * the page that just loaded — and short against a player's patience. A socket
 * that has not completed by then is not slow, it is absent.
 */
const QUEUE_CONNECT_DEADLINE_MS = 10_000;

/**
 * HOW LONG A SEARCH MAY RUN BEFORE THE CLIENT ENDS IT ITSELF.
 *
 * The server already bounds this properly: `BOT_FALLBACK_AFTER_MS` is 30 s (the
 * instant the Sigil band goes unlimited, so a pass that found nobody at 30 s
 * had nobody to find), and at 30 s `MatchOrchestrator` takes the player out of
 * the queue and seats them against a seeded bot account near their rank. When
 * the arena is reachable, that fires first and this timer never does.
 *
 * This is the bound for when the SERVER'S bound does not arrive: the listener
 * is not running (`RGA_MATCH_WS` unset — the dev stack's state when this was
 * reported), the fallback is switched off (`RGA_BOT_OPPONENTS=0`, likewise), or
 * the socket opened and then went quiet. 45 s is deliberately BEHIND the
 * server's 30 s by a margin wide enough for the room to be built and
 * `match_start` to land, so the client never pre-empts a real match; but it is
 * well short of the queue's own 60 s `QUEUE_TIMEOUT_MS`, because a player who
 * has watched a ring for 45 seconds has been told nothing for too long already.
 *
 * Reaching it is NOT a cancel. The player is offered a match against the arena,
 * and the ladder ticket is deliberately left alone — if a human turns up at 50 s
 * the `match_start` still arrives and is still taken. The offer is a floor
 * under the wait, not an end to it.
 */
const QUEUE_SEARCH_DEADLINE_MS = 45_000;

/**
 * THE FALLBACK EIGHT — used only when the player's own deck cannot be read.
 *
 * This list used to be what every online match was PLAYED with, under the
 * comment "Deck building is Phase 5". Deck building shipped; this line did not
 * notice. See `activeDeck()` below for the defect that caused and how it was
 * measured.
 */
const ONLINE_DECK: readonly string[] = Object.freeze([
  'ember-tinderling',
  'neutral-mote-runner',
  'thorn-barbsling',
  'ember-cinderhound',
  'ember-kilnmortar',
  'neutral-ironward',
  'hollow-gravebell',
  'hollow-charnel-ox',
]);

/**
 * THE PLAYER'S ACTIVE DECK, or the fallback eight.
 *
 * ── THE DEFECT THIS EXISTS TO FIX ───────────────────────────────────────────
 * The owner, verbatim: "In decks, i changed card from kilnmortar to other card,
 * but in the game kilnmortar still appeared".
 *
 * He was right, and it was not a caching problem — the deck editor was never
 * consulted at all. BOTH match entry points named a hard-coded list:
 * training started with `matchMod.STARTER_DECK` for both seats, and the online
 * path queued with `ONLINE_DECK` unless a mode-lobby seat ticket happened to
 * carry a deck (a DRAFT does; the deck editor does not). Both lists contain
 * `ember-kilnmortar` — which is precisely why that one card would not go away
 * no matter what he saved. A player could rebuild his deck from scratch and
 * play the starter eight forever, with nothing anywhere reporting an error.
 *
 * ── WHY THE GUARDS ARE WHAT THEY ARE ────────────────────────────────────────
 * `GET /decks` answers `{slots, activeSlot}` and each slot carries the server's
 * own verdicts: `legal` (`@rga/sim`'s ruling on the stored eight) and `ownsAll`
 * (every card still owned). An ILLEGAL or INCOMPLETE deck is not sent to a
 * match: the server would refuse the seat and the player would be bounced out
 * of a queue he had already waited in, which is a worse failure than playing
 * the fallback. The length is checked against the fallback rather than against
 * a literal 8 so the two can never disagree.
 *
 * TOLERANT BY DESIGN. Any failure — offline, 401, a slot that is null, a
 * malformed body — returns the fallback rather than throwing, because the one
 * thing worse than the wrong deck is no match at all. It is logged, so a
 * silently-wrong deck leaves a trace in the console the next time this is
 * investigated.
 */
async function activeDeck(fallback: readonly string[]): Promise<readonly string[]> {
  try {
    const { httpDecksApi } = await import('./decks/decks-api');
    const collection = await httpDecksApi.getDecks();
    const slot = collection.slots[collection.activeSlot];
    if (slot == null) return fallback;
    if (!slot.legal || !slot.ownsAll) {
      console.warn(
        `[rga] active deck in slot ${collection.activeSlot} is not playable ` +
          `(legal=${slot.legal}, ownsAll=${slot.ownsAll}); using the fallback eight`,
      );
      return fallback;
    }
    if (slot.cards.length !== fallback.length) return fallback;
    return slot.cards;
  } catch (err: unknown) {
    console.warn('[rga] could not read the active deck; using the fallback eight', err);
    return fallback;
  }
}

/**
 * THE MATCH ART, FETCHED BY THE SCREEN THAT OWNS THE WAIT.
 *
 * ── WHAT WAS ACTUALLY WRONG ─────────────────────────────────────────────────
 * Not that nothing loaded. `startMatch` has loaded its own art since
 * "generated art reaches a running match for the first time" — it does it
 * whenever `assets` is undefined and `arenaId` is set, which is exactly how
 * this file has always called it. What it could not do is SHOW the wait: the
 * whole fetch happened after `await import('./match')`, behind a
 * one-line "entering the arena" paragraph, with no progress and no way to overlap the
 * two. And nothing anywhere called `loadSheetTimings`, so `sheetTimings` was
 * always absent and NO CARD ATTACK-ANIMATED even when its frames were packed.
 *
 * Loading here fixes both: the progress is a real byte fraction on the real
 * loading screen, and the timings ride along on the same `AssetIo`.
 *
 * ── NEVER FATAL ─────────────────────────────────────────────────────────────
 * `loadMatchAssets` does not reject for a missing manifest, a 404 atlas or a
 * corrupt frame table — it answers with a placeholder set and the match draws
 * the Phase 2 shapes, which is the correct and COMMON state of a checkout with
 * no `tools/assetgen/out`. The only rejection it owns is an abort, and there is
 * none here. The `try` below is for the two chunk imports, and its failure path
 * is a caller that passes no `assets` at all — which lands back on
 * `startMatch`'s own loader, i.e. the behaviour that shipped.
 *
 * Exported for `test/match-assets.test.ts`: this is the whole of the shell's
 * half of the asset handover, and it was unreachable from any assertion while
 * it lived inside `showMatch`.
 */
export interface MatchArt {
  readonly assets: MatchAssets;
  /** Absent (null) means "no card attack-animates" — see `MatchOptions`. */
  readonly sheetTimings: SheetTimingTable | null;
}

export async function loadMatchArt(
  arenaId: string,
  decks: readonly (readonly string[])[],
  onProgress?: (fraction: number) => void,
): Promise<MatchArt> {
  const [loader, timing, cards] = await Promise.all([
    import('./match/assets/loader'),
    import('./match/assets/clip-timing'),
    import('@rga/sim/cards/index'),
  ]);
  const io = loader.browserIo();

  let assets: MatchAssets;
  try {
    assets = await loader.loadMatchAssets(
      {
        arenaId,
        decks,
        /*
         * The spawn closure needs to know which cards summon which. `CARDS`
         * satisfies `SpawnSource` structurally at runtime; the cast is for the
         * checker only, because `Ability` is a union whose members do not all
         * carry `cardId`. Without it a match whose Charnel Ox dies would have
         * no art for the Marrowling it leaves behind.
         */
        cards: cards.CARDS as unknown as ReadonlyMap<
          string,
          { readonly abilities?: readonly { readonly cardId?: string }[] }
        >,
      },
      {
        io,
        ...(onProgress === undefined ? {} : { onProgress: (p) => onProgress(p.fraction) }),
      },
    );
  } catch (err) {
    // Defensive: documented as never rejecting without an abort signal.
    return {
      assets: loader.placeholderAssets(`the match art could not be loaded (${String(err)})`),
      sheetTimings: null,
    };
  }

  /*
   * The rig timings, for the cards that actually have packed frames.
   * `plan.cardIds` already excludes the ones that fell back to shapes, so this
   * is at most two small JSON files per drawable card and usually zero.
   */
  if (assets.manifest === null || assets.plan === null) return { assets, sheetTimings: null };
  try {
    return {
      assets,
      sheetTimings: await timing.loadSheetTimings(assets.manifest, assets.plan.cardIds, io),
    };
  } catch {
    // A missing sheet meta costs an attack animation, never the match.
    return { assets, sheetTimings: null };
  }
}

/** The arena every match in this build wears. Arena 0, unlocked at 0 Sigils. */
const ARENA_ID = 'riftgate-span';

let currentTeardown: (() => void) | null = null;

/** Human-readable one-liner for the menu's "last result" line. */
function describeOutcome(o: MatchOutcome, profileName: string): string {
  const verdict = o.draw ? 'Draw' : o.playerWon ? 'Victory' : 'Defeat';
  const mine = o.viewSide === 0 ? o.sigilsA : o.sigilsB;
  const theirs = o.viewSide === 0 ? o.sigilsB : o.sigilsA;
  // 20 is TICK_HZ, written out rather than imported: this file is the SHELL
  // chunk, and `import { TICK_HZ } from '@rga/sim'` would drag the sim barrel
  // (whose module init builds twelve flow fields) into first paint. The same
  // reason `o.viewSide === 0` is a literal instead of `SIDE_A`.
  const mins = Math.floor(o.ticks / 20 / 60);
  const secs = Math.floor(o.ticks / 20) % 60;
  return `${verdict} vs ${profileName} — ${mine}–${theirs} sigils in ${mins}:${String(secs).padStart(2, '0')}`;
}

/**
 * The one-liner the menu shows after an online match.
 *
 * The SERVER's `match_end` is the authority for the Sigil movement and the
 * share link; the local outcome only supplies the verdict wording, and the two
 * agree because they are the same match.
 */
function describeOnlineOutcome(
  o: MatchOutcome,
  end: { readonly trophyDeltaA: number; readonly trophyDeltaB: number; readonly replayId: string } | null,
): string {
  const verdict = o.draw ? 'Draw' : o.playerWon ? 'Victory' : 'Defeat';
  const mine = o.viewSide === 0 ? o.sigilsA : o.sigilsB;
  const theirs = o.viewSide === 0 ? o.sigilsB : o.sigilsA;
  const delta = end === null ? 0 : o.viewSide === 0 ? end.trophyDeltaA : end.trophyDeltaB;
  const move = delta === 0 ? '' : ` (${delta > 0 ? '+' : ''}${delta} Sigils)`;
  return `${verdict} — ${mine}\u2013${theirs} sigils${move}`;
}

function notice(doc: Document, text: string): HTMLElement {
  const el = doc.createElement('div');
  // `min-h-[60dvh]`, not `h-full`. `#app` is a growing flex item rather than a
  // box pinned to the viewport (see `styles/app.css`), so a percentage height
  // here has nothing definite to resolve against and would collapse to zero —
  // a centred message centred in nothing.
  el.className = 'grid place-items-center min-h-[60dvh] text-sm text-text-dim';
  el.setAttribute('role', 'status');
  el.textContent = text;
  return el;
}

/**
 * Mount the game. Returns a teardown that leaves `root` exactly as it found it —
 * the router calls it on every navigation, and a leaked Pixi context or a live
 * 20 Hz clock behind a menu is the bug this contract exists to prevent.
 */
/**
 * WHAT THIS MATCH AWARDED, READ BACK FROM THE SERVER.
 *
 * The server grants the winner a crate when the room closes
 * (`backend/src/game/match/match.module.ts`, "THE CRATE") and stamps it with
 * `sourceRef: matchId`. Nothing pushes that to the client, so the client asks —
 * and it asks by MATCH ID, not by "the newest slot": two awards can land in the
 * same second, and a stale board would otherwise celebrate a crate from an
 * earlier match.
 *
 * Everything here degrades to `null` rather than throwing. A missing beat is a
 * disappointment; a match-end screen that fails to render is a player stuck in
 * a dead arena. And `null` costs nothing real — the crate is already in their
 * slots, because the server put it there.
 *
 * Exported for its test: it is the whole of the client's half of the crate
 * grant, and it was unreachable from any assertion while it was private.
 *
 * The import is dynamic on purpose: this runs once, at the end of a match, and
 * `engine/crates` has no business inside the 150 KB shell budget (ADR-0005).
 */
export async function resolveMatchCrate(matchId: string): Promise<PostMatchReward | null> {
  // Offline and training matches have no server room and therefore no grant.
  if (matchId === '') return null;
  const [{ httpCratesApi }, { META_PATHS }] = await Promise.all([
    import('./crates/crates-api'),
    import('./meta/route'),
  ]);
  const board = await httpCratesApi.board();
  const slot = board.slots.find((s) => s.source === 'match' && s.sourceRef === matchId);
  // Not an error: the player may simply have had all four slots full, which
  // `awardCrate` reports as `slots-full` rather than failing the match.
  if (slot === undefined || slot.crate === null) return null;
  /*
   * THE CRATE'S OWN PICTURE, WHICH ALREADY SHIPS.
   *
   * The four tiers are `cracked-seam`, `warded-cache`, `keepstone-vault` and
   * `riftgate-hoard`, and `public/ui/crate-<slug>.png` is a painted icon for
   * each — ingested with the rest of the UI set and, until now, drawn on no
   * screen. The post-match beat has taken an `artUrl` since it was written and
   * nothing ever passed one, so the ceremony ended on a grey placeholder box
   * after every win. `iconUrl` returns '' for an id this build does not ship,
   * and the beat falls back to the placeholder exactly as before.
   */
  const artUrl = iconUrl(`crate-${slot.crate.slug}`);
  return {
    kind: 'crate-earned',
    nameKey: slot.crate.nameKey,
    slotIndex: slot.slotIndex,
    ...(artUrl === '' ? {} : { artUrl }),
    onView: () => {
      window.history.pushState({}, '', META_PATHS.crates);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
  };
}

export function mountGame(
  root: HTMLElement,
  user: AuthUser,
  onLogout: () => void,
  /*
   * Where the match stage takes its height from. `'container'` is what
   * `main.ts`'s `mountArenaInShell` passes now that the arena lives inside the
   * shell's content column; the onboarding gate and any full-bleed mount leave
   * it at `'viewport'`. Threading it through here rather than sniffing the DOM
   * keeps `createMatchStage` a pure function of its arguments — and without it
   * the whole `'container'` mode was unreachable, so `rga-stage--contained`
   * never reached an element and errata E2's future `app.css` rule would have
   * matched nothing.
   */
  fill: StageFill = 'viewport',
): () => void {
  const doc = root.ownerDocument;
  let disposed = false;

  // Whatever is on screen right now: the menu's teardown, or the match's.
  let screenTeardown: (() => void) | null = null;
  let match: MatchHandle | null = null;
  let lastResult: string | null = null;
  let settings: MenuSettings = {
    mirrored: a11ySettings().mirrored,
    teamPalette: a11ySettings().teamPalette,
  };
  // Bumped on every screen change so a slow dynamic import that resolves after
  // the player has navigated away cannot mount itself on top of the new screen.
  let generation = 0;

  let menuMod: MenuModule | null = null;
  let matchMod: MatchModule | null = null;
  let netMod: NetModule | null = null;

  /**
   * The training menu's own mirror/palette toggles, folded back into the §6
   * store.
   *
   * `menu.ts` predates `engine/a11y` and persists those two settings itself,
   * to THE SAME two `localStorage` keys (`engine/a11y/settings.ts` reuses them
   * deliberately). So a change made there is already durable — but the live
   * store would not know about it until the next boot, and a match started in
   * the same session would follow the stale value. One call closes that.
   */
  function adoptMenuSettings(s: MenuSettings): void {
    settings = s;
    updateA11ySettings({ mirrored: s.mirrored, teamPalette: s.teamPalette });
  }

  function clearScreen(): void {
    screenTeardown?.();
    screenTeardown = null;
    match?.destroy();
    match = null;
    root.innerHTML = '';
  }

  async function showMenu(): Promise<void> {
    const gen = ++generation;
    clearScreen();
    root.appendChild(notice(doc, t('common.loading')));

    menuMod ??= await import('./meta/menu');
    if (disposed || gen !== generation) return;
    settings = menuMod.readMenuSettings();

    root.innerHTML = '';
    const teardown = menuMod.renderMenu({
      host: root,
      username: user.username ?? user.email,
      settings,
      lastResult,
      onSettings: (s) => {
        adoptMenuSettings(s);
      },
      onPlay: (difficulty, s) => {
        adoptMenuSettings(s);
        void showMatch(difficulty);
      },
      onBattle: (s) => {
        adoptMenuSettings(s);
        void showQueue();
      },
      /*
       * THE MENU CARRIES ITS OWN SIGN-OUT ONLY WHEN NOTHING ELSE DOES.
       * `fill: 'container'` means this mount is inside the meta shell, whose
       * left rail already has the exit (§6, and the owner's "log out in the
       * LEFT nav bar"). Passing it here too would put two sign-outs on one
       * screen. On a full-bleed mount — the onboarding/consent path, which
       * mounts no shell — the menu's copy is the ONLY exit and must stay, so
       * this is conditional rather than deleted.
       */
      ...(fill === 'container' ? {} : { onLogout }),
    });
    screenTeardown = teardown;
  }

  /**
   * THE ONLINE PATH: queue -> match -> result, on ONE socket.
   *
   * The session is constructed here and handed to `startMatch` when
   * `match_start` lands, which is the whole of what "online and local differ
   * only by the installed transport" means in practice — from `startMatch`
   * onwards there is not a branch in the renderer, the HUD or the input layer.
   *
   * The queue screen owns the socket until then. If the player cancels, or the
   * socket dies, it is closed here; if a match starts, ownership passes to the
   * match screen and `showMenu` closes it on the way back.
   */
  async function showQueue(): Promise<void> {
    const gen = ++generation;
    clearScreen();
    root.appendChild(notice(doc, t('match.queue.connecting')));
    /*
     * A MODE LOBBY'S SEAT, IF THE PLAYER CAME FROM ONE.
     *
     * `takePendingSeat` is a POP: a ticket is minted for one lobby and one
     * match, so leaving it behind would make the NEXT visit to the arena try to
     * redeem a dead lobby. With no seat this is `''`, which is exactly what
     * `join()` has always sent — the ladder path is unchanged.
     */
    const seat = takePendingSeat();

    // Both chunks are needed the moment a match starts, and fetching them while
    // the player waits in the queue is free — the alternative is a stall
    // between `match_start` and the first frame.
    //
    // `ensureCardTableFresh` joins the same wait on purpose (ADR-0004): if an
    // operator has published a new card table, the client fetches and VALIDATES
    // it here, before the join, instead of being refused at the gate with
    // `REASON_CARD_TABLE_STALE` and showing the player an error it could have
    // fixed. It resolves immediately when the bundled table is already current,
    // which is every case until somebody publishes — and it never rejects.
    const [menu, net, match] = await Promise.all([
      menuMod ?? import('./meta/menu'),
      netMod ?? import('./match/net'),
      matchMod ?? import('./match'),
      ensureCardTableFresh().catch(() => null),
    ]);
    menuMod = menu;
    netMod = net;
    matchMod = match;
    if (disposed || gen !== generation) return;

    const link = net.createSocketLink({
      url: net.matchSocketUrl(arenaOrigin(), user.id, user.username ?? ''),
    });

    let view: QueueView = {
      // The region the client ASKED for. `QueueUpdateMessage` has no region
      // field in protocol version 1 and adding one is a PROTO_VERSION bump, so
      // this is the honest thing to show: with no latency samples the server's
      // `chooseRegion` keeps the client's hint, so the two agree today.
      region: QUEUE_REGION,
      waitedMs: 0,
      ratingBand: 0,
      regionLocked: true,
      queued: 1,
      /*
       * THE BOUND THIS CLIENT IS PREPARED TO KEEP, quoted before the server has
       * quoted its own. It used to start at 0, which the screen renders as no
       * bound at all — no "at most Ns more" line and an empty ring — so the
       * first seconds of every search said nothing about when it ends. The
       * first `queue_update` replaces it with the server's smaller number.
       *
       * It is honest because it is enforced: `searchDeadline` below ends the
       * search at exactly this many milliseconds with an offer of a match.
       */
      etaMs: QUEUE_SEARCH_DEADLINE_MS,
      error: null,
    };
    let screen: QueueScreen | null = null;
    let handedOver = false;

    /*
     * THE TWO TIMERS THAT MAKE THE WAIT FINITE.
     *
     * Both are cleared by `endWait`, which runs on every exit from this screen
     * — a match starting, an error frame, the socket closing, the player
     * cancelling, the screen being torn down — so neither can outlive the
     * search it bounds and fire an offer over whatever mounted next.
     */
    let connectDeadline: ReturnType<typeof setTimeout> | null = null;
    let searchDeadline: ReturnType<typeof setTimeout> | null = null;
    /*
     * THE PLAYER IS ON THEIR WAY OUT — set before the socket is closed on
     * purpose, and checked by `onClose`.
     *
     * `link.close()` emits the close SYNCHRONOUSLY, so a deliberate exit runs
     * `onClose` before the `void showMenu()` on the next line has touched
     * `generation`. Without this the handler treats the player's own Cancel as
     * the arena dropping out and paints a "no opponent, play a practice match"
     * offer over the exit — for the frame or two before the menu mounts, which
     * on a phone is long enough to see and act on.
     */
    let leaving = false;
    function endWait(): void {
      if (connectDeadline !== null) clearTimeout(connectDeadline);
      if (searchDeadline !== null) clearTimeout(searchDeadline);
      connectDeadline = null;
      searchDeadline = null;
    }
    /**
     * Stop waiting and offer a match against the arena, saying why.
     *
     * `timedOut` is the queue screen's start-a-match state, and `detail` rides
     * along as the sentence it shows in place of "no one is queued right now"
     * — which is true when the SERVER said the queue ran out and a fabrication
     * when the truth is that nothing answered. Both keys are ones the
     * catalogue already carries in all eight locales.
     */
    function offerPractice(detail: string | null): void {
      if (leaving || handedOver || disposed || gen !== generation) return;
      endWait();
      view = { ...view, error: detail, timedOut: true };
      screen?.update(view);
    }

    /**
     * THE DECK THIS PLAYER ACTUALLY BUILT.
     *
     * A DRAFT's eight travel with the seat ticket and win outright — they are
     * not owned, not in any deck builder, and the server reads the draft run
     * anyway. For every other mode this is the player's ACTIVE deck, read from
     * `GET /decks`. Until this line existed the client proposed `ONLINE_DECK`,
     * a hard-coded list, and the deck editor changed nothing about what was
     * played; see `activeDeck()` for the measurement.
     *
     * Awaited BEFORE the session is constructed, because the deck is part of
     * the join frame: resolving it afterwards would queue with the fallback and
     * then have nothing to do with the answer.
     */
    const queueDeck =
      seat?.deck != null && seat.deck.length === ONLINE_DECK.length
        ? seat.deck
        : await activeDeck(ONLINE_DECK);

    const session = new net.NetMatchSession({
      transport: link.transport,
      /*
       * A DRAFT's eight cards are not owned and are not in any deck builder, so
       * they travel with the seat. The SERVER decides either way — for a
       * drafted mode it reads the draft run and ignores the frame — but sending
       * the deck the player actually built is the honest proposal.
       */
      deck: queueDeck,
      region: QUEUE_REGION,
      // The version this client will actually SIMULATE on — the bundled table's
      // until `ensureCardTableFresh` above adopted the server's. Hardcoding a
      // `1` here made the join gate's whole purpose unreachable: a client that
      // always claims version 1 is either right by luck or lying.
      cardTableVersion: activeCardTableVersion(),
      onQueueUpdate: (m) => {
        view = {
          region: view.region,
          waitedMs: m.waitedMs,
          ratingBand: m.ratingBand,
          regionLocked: m.regionLocked,
          queued: m.queued,
          etaMs: m.etaMs,
          error: null,
        };
        screen?.update(view);
      },
      onMatchStart: () => {
        if (disposed || gen !== generation) return;
        handedOver = true;
        // The wait is over, however it ends. A search deadline left armed here
        // would fire 45 s into a LIVE MATCH and try to paint a queue screen
        // over it.
        endWait();
        // §9. The step that closes the funnel's biggest gap: how many of the
        // players who waited actually got into a room.
        trackFunnel('match_enter');
        void showOnlineMatch(session, link, gen, queueDeck);
      },
      onError: (reason, fatal, detail) => {
        /*
         * ── THE ENDLESS SEARCH, AND WHERE IT ACTUALLY LIVED ─────────────────
         * `MatchOrchestrator.refuse()` sends the queue timeout as
         * `{ fatal: false }` — a refusal the SESSION can recover from, which is
         * true: the socket stays open and the player may queue again. This
         * handler then dropped every non-fatal frame on its first line, so the
         * one frame that says "the search is over" never reached the screen.
         * The server had already removed the ticket, so no further
         * `queue_update` arrived either: the ring stopped, the seconds froze,
         * and nothing ever came. That is the endless spinner, and it was three
         * words of client code.
         *
         * `isFatalReason(REASON_QUEUE_TIMEOUT)` is `true` in `@rga/proto` — the
         * protocol has always classed it as terminal for the QUEUE — so the
         * gate is that predicate OR the frame's own flag, never the flag alone.
         */
        if (!fatal && !isFatalReason(reason)) return;
        /*
         * §9's `desync_detected`, reported ONCE per match from the only place
         * that observes one. `lastDesync` is set by the session immediately
         * before it calls this, so reading it here is synchronous and cannot
         * race. A non-desync fatal error leaves it null and reports nothing.
         */
        const desync = session.lastDesync;
        if (desync !== null) reportDesync(desync);
        /*
         * A LADDER TIMEOUT IS NOT AN ERROR — it is "we found no one" (audit #15).
         * On the dev stack (one player) it is the ONLY outcome of Battle, so a
         * bare `REASON_QUEUE_TIMEOUT` error with a lone Cancel was a guaranteed
         * dead end. `timedOut` flips the queue screen to a start-a-match offer
         * whose `onPractice` runs the SAME offline bot match as Modes→Training.
         */
        endWait();
        if (reason === REASON_QUEUE_TIMEOUT) {
          view = { ...view, error: null, timedOut: true };
        } else {
          view = { ...view, error: detail === '' ? t('match.queue.refused') : detail };
        }
        screen?.update(view);
      },
      onClose: () => {
        if (handedOver || disposed || gen !== generation) return;
        // A close that follows a timeout must not clobber the practice offer
        // with a connection-lost error: the socket closing is expected there.
        if (view.timedOut === true) return;
        /*
         * A CLOSED SOCKET IS A FINISHED SEARCH, AND IT MUST STILL OFFER A GAME.
         *
         * This used to set the error and stop, leaving the player on a red row
         * with one Cancel — a dead end that is indistinguishable, from the
         * sofa, from the frozen screen that was reported. The arena being
         * unreachable is exactly the case the practice offer exists for, so it
         * is made here too, carrying the reason as its sentence.
         */
        offerPractice(t('match.queue.lost'));
      },
    });

    root.innerHTML = '';
    screen = menu.renderQueue({
      host: root,
      view,
      onCancel: () => {
        leaving = true;
        endWait();
        session.leaveQueue();
        link.close();
        void showMenu();
      },
      /*
       * NO OPPONENT FOUND → a practice match, reusing `showMatch` (the Modes→
       * Training path). No matchmaking logic is added to the client: this just
       * routes to the offline bot arena the game already ships. The queue
       * socket is released first — the match owns its own transport.
       */
      onPractice: () => {
        leaving = true;
        endWait();
        session.leaveQueue();
        link.close();
        void showMatch(menu.DEFAULT_DIFFICULTY);
      },
    });

    /*
     * ── ARM THE TWO BOUNDS ──────────────────────────────────────────────────
     *
     * Deliberately AFTER `renderQueue`, so `screen` is non-null by the time
     * either can fire, and before `session.join` only because the join is
     * synchronous and the ordering is therefore unobservable.
     *
     * THE CONNECT BOUND reads `link.open`, not an event. `SocketLink` reports
     * the handshake completing through a getter (`socket-link.ts` deliberately
     * keeps no reconnect or connect policy of its own), and a getter is the
     * only thing that can distinguish the failure that was reported: a socket
     * that never opened AND never errored AND never closed, because the server
     * on the other end accepted the TCP connection and then said nothing.
     */
    connectDeadline = setTimeout(() => {
      connectDeadline = null;
      if (link.open || handedOver || disposed || gen !== generation) return;
      // `refused` rather than `lost` — nothing was ever established, so there
      // is no connection to have lost. Both keys exist in all eight locales.
      offerPractice(t('match.queue.refused'));
    }, QUEUE_CONNECT_DEADLINE_MS);
    /*
     * THE SEARCH BOUND. Fires only if the socket is alive and the server has
     * still produced neither a match nor a verdict 15 s past its own 30 s bot
     * fallback. The queue ticket is left where it is on purpose — see
     * `QUEUE_SEARCH_DEADLINE_MS`: an opponent who turns up at 50 s is still
     * taken, because `onMatchStart` is still listening.
     */
    searchDeadline = setTimeout(() => {
      searchDeadline = null;
      offerPractice(null);
    }, QUEUE_SEARCH_DEADLINE_MS);
    /*
     * THE ONE LINE THAT MAKES THREE MODES PLAYABLE.
     *
     * `JoinMessage.ticket` has been in the protocol since Phase 3 and nothing
     * had ever put a value in it. A mode lobby's seat ticket is that value: the
     * server redeems it against `MatchLobbyPort` and seats the pair into the
     * SAME room the ladder uses, on this same socket. An empty string is a
     * ladder join, exactly as before.
     */
    session.join(seat?.ticket ?? '');
    // §9. AFTER the join, so the step means "asked to be paired" rather than
    // "opened a screen that might have". A lobby seat counts: the player is
    // waiting for an opponent either way, and the funnel is about waiting.
    trackFunnel('queue_enter');

    screenTeardown = () => {
      // BEFORE the screen goes: a deadline that survived it would call
      // `screen.update` on a destroyed screen, and — worse — it would do so
      // over whatever mounted in its place.
      endWait();
      screen?.destroy();
      screen = null;
      // Only close the socket if the match never took it over — otherwise this
      // teardown, which runs on every screen change, would hang up mid-match.
      if (!handedOver) link.close();
    };
  }

  /** `match_start` arrived: hand the live session to the renderer. */
  async function showOnlineMatch(
    session: InstanceType<NetModule['NetMatchSession']>,
    link: ReturnType<NetModule['createSocketLink']>,
    fromGen: number,
    /**
     * The eight the player actually queued with — the seat's drafted cards, or
     * their active deck. Passed in rather than re-read here: this must be the
     * SAME list the join frame carried, and a second `GET /decks` could answer
     * differently if the player edited a deck in another tab while queuing.
     */
    queueDeck: readonly string[],
  ): Promise<void> {
    if (disposed || fromGen !== generation) return;
    const gen = ++generation;
    clearScreen();

    /*
     * The determinate wait, in place of the blank frame this path used to show
     * while `startMatch` fetched the atlases inside itself. The socket is
     * already ours and the server is already simulating; the loading screen is
     * what the player looks at instead of nothing.
     *
     * It is also only the FIRST FRAME of the wait: `match/ceremony` takes the
     * screen over as soon as its (DOM-only, Pixi-free) chunk lands, and the
     * plain loading screen stays as the thing that covers that one import.
     */
    const loading = mountLoadingScreen(root, { kind: 'match', progress: 0 });
    let ceremony: MatchCeremony | null = null;
    let loadingLive = true;
    const setLoadProgress = (f: number): void => {
      if (ceremony !== null) ceremony.setProgress(f);
      else if (loadingLive) loading.setProgress(f);
    };
    const dropWait = (): void => {
      ceremony?.destroy();
      ceremony = null;
      if (loadingLive) loading.destroy();
      loadingLive = false;
    };
    screenTeardown = () => {
      dropWait();
      link.close();
    };

    const mod = matchMod ?? (await import('./match'));
    matchMod = mod;
    if (disposed || gen !== generation) return;

    /*
     * OUR EIGHT, not the pair's sixteen: `match_start` does not carry the
     * opponent's list in protocol version 1, and a card whose art is not in the
     * working set falls back to its Phase 2 shape per-card rather than blanking
     * the board. The props band — every tower and every gutter decor — is
     * planned unconditionally by `planLoad`, so it is unaffected either way.
     *
     * STARTED, NOT AWAITED: this promise is the CEREMONY'S GATE. The player
     * lines, the VS turn and "Fight!" run after it settles, so the sequence can
     * neither delay a match that was ready nor be cut off by a load that took
     * longer than a fixed script.
     */
    // The deck the player is ACTUALLY queued with, not the fallback eight: this
    // is what decides which card art is warmed, and warming the wrong eight
    // means every card the player really built pops in mid-match.
    const artLoad = loadMatchArt(ARENA_ID, [queueDeck], setLoadProgress).catch(
      (err: unknown) => {
        console.warn('[rga] match art could not be loaded; drawing shapes', err);
        return null;
      },
    );

    /*
     * STAGE 2 — the presentation. Its own dynamic import: it is DOM-only, so it
     * can paint while the Pixi chunk is still settling, and keeping it out of
     * the shell keeps the 150 KB budget where it is (ADR-0005).
     *
     * The two lines are REAL: `match_start` carries `players[2]` with a `name`
     * and a `cardLevel` per side, and `session.side` says which of the two is
     * ours. Nothing on this screen is invented — see the note below about the
     * one field the wire does not carry.
     */
    const cerMod = await import('./match/ceremony').catch((err: unknown) => {
      console.warn('[rga] the ceremony chunk could not be loaded', err);
      return null;
    });
    const started = session.matchStart;
    if (!disposed && gen === generation && cerMod !== null && started !== null) {
      const usSide = session.side;
      const us = started.players[usSide];
      const them = started.players[usSide === 0 ? 1 : 0];
      const frames = await cerMod.loadVsFrames();
      /*
       * The arena line, from the META chunk's own cache. DYNAMIC, not a static
       * import: `meta-api` in the shell's graph would move a screen's worth of
       * code in front of first paint and past the 150 KB budget (ADR-0005). By
       * here the meta chunk is long since loaded, so this costs nothing.
       */
      const metaApi = await import('./meta/meta-api').catch(() => null);
      const metaI18n = await import('./meta/meta-i18n').catch(() => null);
      const cachedArenaId = metaApi?.peekCollection()?.arena.id ?? null;
      /*
       * The TRANSLATED arena name, exactly as every other screen renders it
       * (`arenaLabel` → `t('ladder.arena.<id>.name')`). `arenaLabel` falls back
       * to the raw id when the catalogue is behind; the ceremony must never show
       * that slug, so when the label is still the id we render nothing at all.
       */
      const arenaLabel =
        cachedArenaId !== null && metaI18n !== null ? metaI18n.arenaLabel(cachedArenaId) : null;
      const arenaName = arenaLabel !== null && arenaLabel !== cachedArenaId ? arenaLabel : null;
      if (!disposed && gen === generation) {
        ceremony = cerMod.runMatchCeremony({
          host: root,
          you: { name: us.name === '' ? (user.username ?? '') : us.name, level: us.cardLevel },
          opponent: { name: them.name, level: them.cardLevel },
          /*
           * The ARENA the pair are fighting in. `match_start` carries no arena
           * id in protocol version 1 — it carries a seed, decks, sides and the
           * two players — so this is the arena the META screens last told this
           * client it was in, and it is omitted rather than guessed when no
           * collection has been read yet. It is the TRANSLATED name (`nameKey`,
           * as every other screen renders it), never the raw slug/id — and when
           * there is no cache, or no translation for the key, nothing is shown.
           */
          arenaName,
          ready: artLoad,
          frames,
        });
        if (loadingLive) loading.destroy();
        loadingLive = false;
      }
    }

    const art = await artLoad;
    if (disposed || gen !== generation) {
      dropWait();
      return;
    }
    /*
     * THE HANDOFF. The match is BUILT after this resolves, which is what makes
     * "the match must not accept input until the ceremony releases it" true by
     * construction rather than by a flag: until here there is no renderer, no
     * `PointerInput` and no hand. A skip collapses the timed beats only; it
     * cannot reach past this await into a match that is not ready.
     */
    if (ceremony !== null) {
      try {
        await ceremony.released;
      } catch {
        // The ceremony was torn down or failed mid-handoff (a teardown, a
        // rejected gate). It rejects `released` deliberately rather than leaving
        // this await pending forever, so we abandon the build instead of
        // stalling on a permanent black frame.
        dropWait();
        return;
      }
    }
    if (disposed || gen !== generation) {
      dropWait();
      return;
    }
    dropWait();

    const stage = createMatchStage(doc, fill);
    root.innerHTML = '';
    root.appendChild(stage.frame);

    /*
     * WHETHER THIS CLIENT RECONNECTED, read at match end, not now.
     *
     * If the socket dropped and the grace loop below got us back, the server
     * may hand us the finished match as an `endedMatchFrame` whose ladder delta
     * is a sentinel 0 (`backend/.../reconnect.ts`) — it means "unknown to you",
     * not "no movement". The end ceremony must drop the ladder block in that
     * case, so it needs to know we resumed. The reconnect happens seconds after
     * `startMatch`, so this is a live getter, not a value captured here.
     */
    let reconnected = false;

    /*
     * THE EQUIPPED EMOTES for the wheel, fetched once and tolerant: a failed
     * loadout read (or an account with none) leaves the wheel on its default set.
     * `mod.hudEmotesFromIds` takes the loadout's FULL ITEM IDS
     * (`cosmetic:emote:<slug>`) as well as bare slugs, and keeps only the ones
     * that have a protocol slot, so an emote that cannot be sent is never
     * offered. Dynamic import so the shop chunk never enters the shell budget
     * (ADR-0005).
     */
    let equippedEmotes: readonly import('./match/render/hud').HudEmote[] = [];
    /*
     * THE EQUIPPED ARENA SKIN, from the SAME loadout read. Purely cosmetic and
     * purely the player's own: it re-dresses the background this client draws
     * and never reaches the sim (see `startMatch`'s `arenaSkin` note). Read
     * defensively — a loadout with no arena-skin field just leaves it null and
     * the board wears the base arena art.
     */
    let equippedArenaSkin: string | null = null;
    /*
     * THE EQUIPPED TOWER SKIN, from that same single loadout read, on exactly
     * the arena skin's terms: the player's own cosmetic, re-dressing only which
     * baked prop stands on each tower pad in THIS client (see `startMatch`'s
     * `towerSkin` note). Null leaves the base towers standing.
     */
    let equippedTowerSkin: string | null = null;
    /*
     * THE OPPONENT'S TOWER SKIN — the one cosmetic on this screen that is NOT
     * read from our own loadout, because it is not ours.
     *
     * `getLoadout()` is self-only and there is no endpoint that hands you
     * another player's wardrobe (deliberately: a wardrobe is not public). So the
     * server puts it in the frame that seats us: `match_start.dress` is a
     * two-element array indexed by `Side`, exactly like `match_start.players`,
     * filled from the other seat's own equipped skin by
     * `backend/src/game/presentation/`. COSTS NOTHING HERE — `session.matchStart`
     * is already read a few lines above for the ceremony, so there is no second
     * fetch and no await.
     *
     * ABSENT IS THE COMMON CASE AND IS CORRECT: `dress` is optional on the wire
     * (a server older than proto v3 sends none), a bot seat is sent as the empty
     * string, and an undressed player is sent as the empty string too. All three
     * land on null, which is "they wear the base towers".
     */
    let opponentTowerSkin: string | null = null;
    {
      const startedForDress = session.matchStart;
      const theirSeat = session.side === 0 ? 1 : 0;
      const theirDress = startedForDress?.dress?.[theirSeat]?.tower ?? '';
      opponentTowerSkin = theirDress === '' ? null : theirDress;
    }
    /*
     * THE EQUIPPED DEPLOY EFFECT, off that same single loadout read and on the
     * same terms as the two skins above: the player's own cosmetic, dressing
     * only the particles THIS client draws when THIS player's card lands (see
     * `startMatch`'s `deployVfx` note). Null leaves STYLE_BIBLE §6's dust.
     *
     * It is read here for the same reason the tower skin is: the loadout comes
     * back with the field on it either way, and the shop has been selling these
     * for 600–6 000 Shards while this line did not exist — the id was stored,
     * returned, and then dropped on the floor between here and the match.
     */
    let equippedDeployVfx: string | null = null;
    try {
      const { httpShopApi } = await import('./shop/shop-api');
      const loadout = await httpShopApi.getLoadout();
      equippedEmotes = mod.hudEmotesFromIds(loadout.emotes);
      equippedArenaSkin = (loadout as { arenaSkin?: string | null }).arenaSkin ?? null;
      equippedTowerSkin = loadout.towerSkin ?? null;
      equippedDeployVfx = loadout.deployVfx ?? null;
    } catch {
      /* no loadout — the wheel keeps its default set and the arena its base art */
    }

    let handle: MatchHandle;
    try {
      handle = await mod.startMatch({
        resumed: () => reconnected,
        ...(equippedEmotes.length === 0 ? {} : { emotes: equippedEmotes }),
        // Kept even though `assets` is supplied below: it is also what names
        // the arena background and the music layers.
        arenaId: ARENA_ID,
        // COSMETIC, LOCAL: the player's own equipped skin re-dresses the
        // background only. Absent → the base arena art.
        ...(equippedArenaSkin === null ? {} : { arenaSkin: equippedArenaSkin }),
        // COSMETIC: the player's own tower skin re-dresses THEIR OWN three
        // tower props. Absent → their base towers.
        ...(equippedTowerSkin === null ? {} : { towerSkin: equippedTowerSkin }),
        // COSMETIC, and the one that is NOT local: the opponent's own tower
        // skin, off `match_start.dress`, dressing the other three pads. Absent
        // → base towers on their end, which is what a bot and an undressed
        // player both get. Passing the local skin here instead is the bug this
        // pair replaced — it painted your purchase onto their towers.
        ...(opponentTowerSkin === null ? {} : { opponentTowerSkin }),
        // COSMETIC, LOCAL, same terms again: the player's own deploy effect
        // re-dresses the particles on their OWN deploy beat, and nobody else's.
        // Absent → §6's dust, which is what everyone saw before this line.
        ...(equippedDeployVfx === null ? {} : { deployVfx: equippedDeployVfx }),
        /*
         * ART, ALREADY LOADED. Passing it stops `startMatch` fetching the same
         * manifest a second time, and `null` is never passed here — a failed
         * load hands over the placeholder set, not nothing.
         */
        ...(art === null ? {} : { assets: art.assets, sheetTimings: art.sheetTimings }),
        host: stage.host,
        session,
        hudSettings: { mirrored: settings.mirrored },
        teamPalette: settings.teamPalette,
        onExit: () => {
          link.close();
          void showMenu();
        },
        resolveReward: resolveMatchCrate,
      });
    } catch (err) {
      console.error('startMatch (online) failed', err);
      link.close();
      if (disposed || gen !== generation) return;
      lastResult = t('match.queue.device_failed');
      void showMenu();
      return;
    }

    if (disposed || gen !== generation) {
      handle.destroy();
      link.close();
      return;
    }

    match = handle;

    /*
     * THE 15 SECOND GRACE, FROM THIS SIDE (§7, ADR-0002).
     *
     * The server keeps the seat, holds it with a bot that deploys nothing, and
     * forfeits at `RECONNECT_GRACE_MS`. So the client's job is to get a socket
     * back inside that window and say `resume` — and the predictor, its state
     * and its input log survive the swap, because a match IS
     * `{seed, decks, inputLog}` and there is no frame in this protocol that
     * carries a board to be handed back.
     *
     * Backoff is linear and deliberately short: there is no point being polite
     * to a server that will forfeit us in fifteen seconds.
     */
    let live = true;
    let attempts = 0;
    let currentLink = link;
    const reconnect = (): void => {
      if (!live || disposed || gen !== generation) return;
      const net = netMod;
      if (net === null || session.phase === net.NET_ENDED) return;
      const elapsed = attempts * RECONNECT_RETRY_MS;
      if (elapsed >= RECONNECT_GRACE_LIMIT_MS) return;
      attempts++;
      reconnected = true;
      globalThis.setTimeout(() => {
        if (!live || disposed || gen !== generation) return;
        currentLink = net.createSocketLink({
          url: net.matchSocketUrl(arenaOrigin(), user.id, user.username ?? ''),
        });
        currentLink.transport.onClose(() => reconnect());
        session.attach(currentLink.transport);
      }, RECONNECT_RETRY_MS);
    };
    currentLink.transport.onClose(() => reconnect());

    screenTeardown = () => {
      live = false;
      handle.destroy();
      currentLink.close();
    };

    void handle.finished.then((outcome) => {
      live = false;
      if (disposed || gen !== generation) return;
      const end = session.matchEnd;
      lastResult = describeOnlineOutcome(outcome, end);
    });
  }


  async function showMatch(difficulty: number): Promise<void> {
    const gen = ++generation;
    clearScreen();
    /*
     * A DETERMINATE WAIT, not a sentence. This used to be one untranslated
     * paragraph naming the arena, while the match chunk AND every
     * atlas landed behind it. `mountLoadingScreen` is the component that
     * already exists for this, it carries the catalogue's own strings, and it
     * honours `prefers-reduced-motion` itself.
     */
    const loading = mountLoadingScreen(root, { kind: 'match', progress: 0 });
    screenTeardown = () => {
      loading.destroy();
    };

    // THE lazy import. Pixi, the sim and every render module arrive here and
    // nowhere else in the statically-reachable graph.
    matchMod ??= await import('./match');
    if (disposed || gen !== generation) return;

    /*
     * The training deck is `startMatch`'s own default for BOTH seats, so the
     * working set is that eight closed under what they can spawn. Reading it
     * off the module rather than restating it here keeps one list: a deck
     * copied into the shell would drift the day the starter changes and the
     * only symptom would be units that draw shapes.
     */
    /*
     * THE PLAYER'S OWN DECK, not the starter eight.
     *
     * This line read `matchMod.STARTER_DECK` and gave it to BOTH seats, so
     * training ignored the deck editor completely — the owner's "I changed the
     * card and kilnmortar still appeared" was most visible here, because
     * training is where you go to look at a deck you just changed. The bot
     * still fights with the starter eight (see `startMatch` below): a known,
     * fixed opposing deck is what makes training a place to test a change.
     */
    const deck = await activeDeck(matchMod.STARTER_DECK);
    const art = await loadMatchArt(ARENA_ID, [deck, deck], (f) => loading.setProgress(f)).catch(
      (err: unknown) => {
        console.warn('[rga] match art could not be loaded; drawing shapes', err);
        return null;
      },
    );
    if (disposed || gen !== generation) {
      loading.destroy();
      return;
    }
    loading.destroy();

    const stage = createMatchStage(doc, fill);
    root.innerHTML = '';
    root.appendChild(stage.frame);

    /*
     * The player's equipped arena skin dresses training too — a skin is the
     * player's own cosmetic, so it applies against the bot exactly as online.
     * Tolerant: a failed read just leaves the base arena art.
     */
    let equippedArenaSkin: string | null = null;
    let equippedTowerSkin: string | null = null;
    /*
     * The deploy effect dresses training too, for the arena skin's reason: it
     * is the player's own cosmetic and applies against the bot exactly as it
     * does online. Training is also where a player goes to LOOK at something
     * they just bought, so this is the call site that most needs to carry it.
     */
    let equippedDeployVfx: string | null = null;
    try {
      const { httpShopApi } = await import('./shop/shop-api');
      const loadout = await httpShopApi.getLoadout();
      equippedArenaSkin = (loadout as { arenaSkin?: string | null }).arenaSkin ?? null;
      equippedTowerSkin = loadout.towerSkin ?? null;
      equippedDeployVfx = loadout.deployVfx ?? null;
    } catch {
      /* no loadout — the arena keeps its base art and the towers theirs */
    }

    let handle: MatchHandle;
    try {
      handle = await matchMod.startMatch({
        /*
         * THE DECK THE PLAYER BUILT, HANDED TO THE SIMULATION.
         *
         * `activeDeck()` above resolved it, and until this line the result was
         * used ONLY to warm the art: `startMatch` fell through to its own
         * `opts.playerDeck ?? STARTER_DECK` default, so training loaded the
         * right pictures and then played the starter eight. That is why the
         * owner could replace Kilnmortar, watch the save succeed, and still
         * meet Kilnmortar in the next practice match.
         *
         * The BOT keeps the starter eight (`botDeck` is left unset): a known,
         * fixed opposing deck is what makes training a place to test a change.
         */
        playerDeck: deck,
        // Still passed with `assets`: it also names the arena background and
        // the arena's music layers.
        arenaId: ARENA_ID,
        ...(equippedArenaSkin === null ? {} : { arenaSkin: equippedArenaSkin }),
        ...(equippedTowerSkin === null ? {} : { towerSkin: equippedTowerSkin }),
        ...(equippedDeployVfx === null ? {} : { deployVfx: equippedDeployVfx }),
        // See `showOnlineMatch`. A failed load hands over the placeholder set.
        ...(art === null ? {} : { assets: art.assets, sheetTimings: art.sheetTimings }),
        host: stage.host,
        difficulty,
        /*
         * ADR-0004 offline too. A balance hot-patch that applied to ranked play
         * and not to training would make the practice mode a lie about the game
         * it is practice for. `activeCards()` is `undefined` until a table has
         * been fetched and validated, and `createMatch` then uses the bundled
         * roster — which is exactly what happened before this line existed.
         */
        ...(activeCards() === undefined ? {} : { cards: activeCards() }),
        hudSettings: { mirrored: settings.mirrored },
        teamPalette: settings.teamPalette,
        onExit: () => {
          void showMenu();
        },
      });
    } catch (err) {
      // A device with no WebGL at all, or a renderer that refused to init.
      console.error('startMatch failed', err);
      if (disposed || gen !== generation) return;
      root.innerHTML = '';
      lastResult = t('match.queue.device_failed');
      void showMenu();
      return;
    }

    if (disposed || gen !== generation) {
      handle.destroy();
      return;
    }

    match = handle;
    screenTeardown = () => {
      handle.destroy();
    };

    const profileName = handle.profile.name;
    void handle.finished.then((outcome) => {
      if (disposed || gen !== generation) return;
      lastResult = describeOutcome(outcome, profileName);
      // The result card stays up; `onExit` takes the player back to the menu.
    });
  }

  /*
   * WATCH A FRIEND'S LIVE MATCH — the spectator's mirror of the seat handoff.
   *
   * `engine/meta/screens.ts` may not import `engine/match/**` (ADR-0005), so it
   * stashes the target (`engine/social/spectate-handoff.ts`) and navigates
   * here; this pops it and mounts the DOM spectator surface. The surface is its
   * own dynamic chunk, so nothing it pulls in enters the shell budget.
   */
  async function showSpectate(target: SpectatorTarget): Promise<void> {
    const gen = ++generation;
    clearScreen();
    root.appendChild(notice(doc, t('common.loading')));

    const mod = await import('./social/spectate-screen');
    if (disposed || gen !== generation) return;

    root.innerHTML = '';
    screenTeardown = mod.renderSpectateScreen(root, {
      target,
      onClose: () => {
        void showMenu();
      },
      onOpenReplay: (replayId) => {
        window.history.pushState({}, '', `/replay/${replayId}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      },
    });
  }

  /*
   * ── WHERE THE ARENA OPENS, AND WHY IT IS NOT ALWAYS THE MENU ──────────────
   *
   * The modes hub decides things this screen has to honour, and until this
   * existed it honoured none of them: every entry opened the local menu.
   *
   *   A LOBBY SEAT. Two friends press "play" in a lobby, a ticket is minted for
   *   each, and both were dropped on the arena's TRAINING menu — a difficulty
   *   list — where they had to find "Battle" to reach the waiting room their
   *   ticket was for. `peekPendingSeat` was written for exactly this check and
   *   had no caller. PEEK, not pop: `showQueue()` pops it, and popping twice
   *   would spend the seat on the way to using it.
   *
   *   A TRAINING DIFFICULTY. The hub's panel asks which of the five profiles to
   *   play and the answer was discarded at the router, so the menu asked again.
   *
   * With neither pending this is `showMenu()`, unchanged — the ladder and the
   * cold-start paths open exactly where they always did.
   */
  const pendingTraining = takePendingTraining();
  const pendingSpectate = takePendingSpectate();
  if (peekPendingSeat() !== null) {
    void showQueue();
  } else if (pendingSpectate !== null) {
    void showSpectate(pendingSpectate);
  } else if (pendingTraining !== null) {
    void showMatch(pendingTraining);
  } else {
    void showMenu();
  }

  currentTeardown = () => {
    if (disposed) return;
    disposed = true;
    generation++;
    clearScreen();
  };
  return currentTeardown;
}

export function unmountGame(): void {
  currentTeardown?.();
  currentTeardown = null;
}
